/**
 * UI样式辅助工具类
 * 统一管理UI样式配置和创建方法，减少重复代码
 */
export default class UIStyleHelper {
    /** 全局像素风字体 */
    static PIXEL_FONT = 'monospace';

    /**
     * 预定义的文本样式
     */
    static TEXT_STYLES = {
        // 标题样式
        TITLE: {
            fontSize: '32px',
            fill: '#4cc9f0',
            fontFamily: 'monospace',
            fontWeight: 'bold'
        },
        
        // 副标题样式
        SUBTITLE: {
            fontSize: '20px',
            fill: '#ffff00',
            fontFamily: 'monospace',
            fontWeight: 'bold'
        },
        
        // 普通文本样式
        NORMAL: {
            fontSize: '16px',
            fill: '#ffffff',
            fontFamily: 'monospace'
        },
        
        // 小文本样式
        SMALL: {
            fontSize: '14px',
            fill: '#aaaaaa',
            fontFamily: 'monospace'
        },
        
        // 提示文本样式
        HINT: {
            fontSize: '16px',
            fill: '#aaaaaa',
            fontFamily: 'monospace'
        },
        
        // 高亮文本样式
        HIGHLIGHT: {
            fontSize: '16px',
            fill: '#ffff00',
            fontFamily: 'monospace',
            fontWeight: 'bold'
        },
        
        // 金钱文本样式
        MONEY: {
            fontSize: '16px',
            fill: '#ffd700',
            fontFamily: 'monospace',
            fontWeight: 'bold'
        },
        
        // 选中项样式
        SELECTED: {
            fontSize: '20px',
            fill: '#ffff00',
            fontFamily: 'monospace',
            padding: { x: 15, y: 8 }
        },
        
        // 未选中项样式
        UNSELECTED: {
            fontSize: '20px',
            fill: '#ffffff',
            fontFamily: 'monospace',
            padding: { x: 15, y: 8 }
        },
        
        // 标签样式
        LABEL: {
            fontSize: '16px',
            fill: '#ffffff',
            fontFamily: 'monospace'
        },
        
        // 错误文本样式
        ERROR: {
            fontSize: '16px',
            fill: '#ff0000',
            fontFamily: 'monospace',
            fontWeight: 'bold'
        },
        
        // 成功文本样式
        SUCCESS: {
            fontSize: '16px',
            fill: '#00ff00',
            fontFamily: 'monospace',
            fontWeight: 'bold'
        }
    };

    /** 菜单交互统一配色（选中/普通/已装备等） */
    static MENU_COLORS = {
        SELECTED: '#ffff00',
        NORMAL: '#ffffff',
        INACTIVE: '#888888',
        EQUIPPED: '#4cc9f0',
        SUCCESS: '#00ff00'
    };

    /** 菜单操作提示文案（与 InputHelper 按键标准一致） */
    static MENU_HINTS = {
        NAVIGATE: 'W/S 或方向键移动',
        SELECT: '空格/Enter 确认',
        CANCEL: 'Esc 返回',
        CATEGORY: 'A/D 或左右方向键切换分类',
        SUBMENU: 'W/S 或方向键选择，空格/Enter 确认，Esc 返回'
    };

    /**
     * 获取菜单项文字颜色
     * @param {boolean} isSelected
     * @param {{ isEquipped?: boolean, isInactive?: boolean }} options
     */
    static getMenuItemFill(isSelected, options = {}) {
        if (isSelected) return this.MENU_COLORS.SELECTED;
        if (options.isInactive) return this.MENU_COLORS.INACTIVE;
        if (options.isEquipped) return this.MENU_COLORS.EQUIPPED;
        return this.MENU_COLORS.NORMAL;
    }

    /**
     * 应用菜单项文字颜色
     */
    static applyMenuItemFill(text, isSelected, options = {}) {
        if (!text) return;
        const fill = this.getMenuItemFill(isSelected, options);
        if (text.setColor) {
            text.setColor(fill);
        } else if (text.setFill) {
            text.setFill(fill);
        } else {
            text.setStyle({ fill });
        }
    }
    
    /**
     * 创建标题文本
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} text - 文本内容
     * @param {number} y - Y坐标（可选，默认60）
     * @param {Object} customStyle - 自定义样式（可选，会合并到默认样式）
     * @returns {Phaser.GameObjects.Text} 文本对象
     */
    static createTitle(scene, text, y = 60, customStyle = {}) {
        const style = { ...this.TEXT_STYLES.TITLE, ...customStyle };
        const title = scene.add.text(
            scene.cameras.main.centerX,
            y,
            text,
            style
        );
        title.setOrigin(0.5);
        return title;
    }
    
    /**
     * 创建副标题文本
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} text - 文本内容
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {Object} customStyle - 自定义样式（可选）
     * @returns {Phaser.GameObjects.Text} 文本对象
     */
    static createSubtitle(scene, text, x, y, customStyle = {}) {
        const style = { ...this.TEXT_STYLES.SUBTITLE, ...customStyle };
        const subtitle = scene.add.text(x, y, text, style);
        subtitle.setOrigin(0.5);
        return subtitle;
    }
    
    /**
     * 创建普通文本
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} text - 文本内容
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {Object} customStyle - 自定义样式（可选）
     * @returns {Phaser.GameObjects.Text} 文本对象
     */
    static createText(scene, text, x, y, customStyle = {}) {
        const style = { ...this.TEXT_STYLES.NORMAL, ...customStyle };
        return scene.add.text(x, y, text, style);
    }
    
    /**
     * 创建提示文本
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} text - 文本内容
     * @param {number} y - Y坐标（可选，默认屏幕底部）
     * @param {Object} customStyle - 自定义样式（可选）
     * @returns {Phaser.GameObjects.Text} 文本对象
     */
    static createHint(scene, text, y = null, customStyle = {}) {
        const style = { ...this.TEXT_STYLES.HINT, ...customStyle };
        const hintY = y !== null ? y : scene.cameras.main.height - 50;
        const hint = scene.add.text(
            scene.cameras.main.centerX,
            hintY,
            text,
            style
        );
        hint.setOrigin(0.5);
        return hint;
    }
    
    /**
     * 创建标签文本
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} text - 文本内容
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {Object} customStyle - 自定义样式（可选）
     * @returns {Phaser.GameObjects.Text} 文本对象
     */
    static createLabel(scene, text, x, y, customStyle = {}) {
        const style = { ...this.TEXT_STYLES.LABEL, ...customStyle };
        return scene.add.text(x, y, text, style);
    }
    
    /**
     * 创建菜单项文本
     * @param {Phaser.Scene} scene - 场景对象
     * @param {string} text - 文本内容
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {boolean} isSelected - 是否选中
     * @param {Object} customStyle - 自定义样式（可选）
     * @returns {Phaser.GameObjects.Text} 文本对象
     */
    static createMenuItem(scene, text, x, y, isSelected = false, customStyle = {}) {
        const baseStyle = isSelected ? this.TEXT_STYLES.SELECTED : this.TEXT_STYLES.UNSELECTED;
        const style = { ...baseStyle, ...customStyle };
        const menuItem = scene.add.text(x, y, text, style);
        menuItem.setOrigin(0.5);
        menuItem.setInteractive();
        return menuItem;
    }
    
    /**
     * 创建金钱显示文本
     * @param {Phaser.Scene} scene - 场景对象
     * @param {number} amount - 金钱数量
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {Object} customStyle - 自定义样式（可选）
     * @returns {Phaser.GameObjects.Text} 文本对象
     */
    static createMoneyText(scene, amount, x, y, customStyle = {}) {
        const style = { ...this.TEXT_STYLES.MONEY, ...customStyle };
        const moneyText = scene.add.text(x, y, `金钱: ${amount} G`, style);
        moneyText.setOrigin(1, 0.5);
        return moneyText;
    }
    
    /**
     * 获取样式配置（用于直接使用）
     * @param {string} styleName - 样式名称
     * @returns {Object|null} 样式配置对象
     */
    static getStyle(styleName) {
        return this.TEXT_STYLES[styleName] || null;
    }
    
    /**
     * 合并样式
     * @param {Object} baseStyle - 基础样式
     * @param {Object} customStyle - 自定义样式
     * @returns {Object} 合并后的样式
     */
    static mergeStyles(baseStyle, customStyle) {
        return { ...baseStyle, ...customStyle };
    }
}

